class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  async open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    // If drawer is currently marked empty, or has no cart items, or flagged for refresh:
    // Fetch latest sections from Shopify Section Rendering API before/while displaying.
    if (this.classList.contains('is-empty') || !this.querySelector('.cart-item') || this._needsRefresh) {
      await this.refresh();
    }

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    // Animation trigger
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement || !parsedState.sections || !parsedState.sections[section.id]) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    if (parsedState.sections && parsedState.sections['cart-drawer']) {
      const parsedDoc = new DOMParser().parseFromString(parsedState.sections['cart-drawer'], 'text/html');
      const newDrawer = parsedDoc.querySelector('cart-drawer');
      if (newDrawer) {
        if (newDrawer.classList.contains('is-empty')) {
          this.classList.add('is-empty');
        } else {
          this.classList.remove('is-empty');
        }
      }
    } else {
      this.classList.remove('is-empty');
    }

    this._needsRefresh = false;

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  async refresh() {
    try {
      const root = window.Shopify?.routes?.root || '/';
      const sectionsUrl = `${root}?sections=cart-drawer,cart-icon-bubble`;
      const response = await fetch(sectionsUrl, { headers: { 'Cache-Control': 'no-cache' } });
      if (!response.ok) return;
      const sections = await response.json();
      if (!sections || !sections['cart-drawer']) return;

      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(sections['cart-drawer'], 'text/html');

      // Sync is-empty class on <cart-drawer>
      const newCartDrawer = parsedDoc.querySelector('cart-drawer');
      if (newCartDrawer) {
        if (newCartDrawer.classList.contains('is-empty')) {
          this.classList.add('is-empty');
        } else {
          this.classList.remove('is-empty');
        }
      }

      // Update inner drawer DOM
      const newInner = parsedDoc.querySelector('#CartDrawer');
      const currentInner = document.getElementById('CartDrawer');
      if (newInner && currentInner) {
        currentInner.innerHTML = newInner.innerHTML;
      }

      // Update header cart bubble
      const bubbleHtml = sections['cart-icon-bubble'];
      if (bubbleHtml) {
        const bubbleDoc = parser.parseFromString(bubbleHtml, 'text/html');
        const newBubble = bubbleDoc.querySelector('#cart-icon-bubble') || bubbleDoc.body.firstElementChild;
        const currentBubble = document.getElementById('cart-icon-bubble');
        if (newBubble && currentBubble) {
          currentBubble.innerHTML = newBubble.innerHTML;
        }
      }

      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
      this._needsRefresh = false;
    } catch (e) {
      console.warn('[CartDrawer] Refresh failed:', e);
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const el = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return el ? el.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

// Global Auto-Sync for Cart Updates (Customizers, Apps, Background AJAX)
(function initGlobalCartAutoSync() {
  if (window.__tms_cart_sync_initialized) return;
  window.__tms_cart_sync_initialized = true;

  const isCartMutationUrl = (url) => {
    if (!url || typeof url !== 'string') return false;
    return (
      (url.includes('/cart/add') || url.includes('/cart/update') || url.includes('/cart/change') || url.includes('/cart/clear')) &&
      !url.includes('?sections=')
    );
  };

  const triggerCartRefresh = () => {
    const drawer = document.querySelector('cart-drawer');
    if (drawer) {
      drawer._needsRefresh = true;
      if (typeof drawer.refresh === 'function') {
        drawer.refresh();
      }
    }
  };

  // 1. Intercept window.fetch
  const originalFetch = window.fetch;
  window.fetch = async function(...args) {
    const response = await originalFetch.apply(this, args);
    try {
      const url = typeof args[0] === 'string' ? args[0] : (args[0]?.url || '');
      if (isCartMutationUrl(url) && response.ok) {
        triggerCartRefresh();
      }
    } catch (err) {}
    return response;
  };

  // 2. Intercept XMLHttpRequest (for jQuery / older apps)
  const originalXhrOpen = XMLHttpRequest.prototype.open;
  const originalXhrSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function(method, url, ...rest) {
    this._tms_request_url = url;
    return originalXhrOpen.call(this, method, url, ...rest);
  };

  XMLHttpRequest.prototype.send = function(...args) {
    this.addEventListener('load', function() {
      try {
        if (isCartMutationUrl(this._tms_request_url) && this.status >= 200 && this.status < 300) {
          triggerCartRefresh();
        }
      } catch (err) {}
    });
    return originalXhrSend.apply(this, args);
  };

  // 3. Global capture listener for any "Ver carrinho" clicks / cart links / customizer confirmation screens
  const isDrawerElement = (el) => {
    if (!el || !(el instanceof Element)) return false;
    return (
      el.tagName === 'CART-DRAWER' ||
      el.id === 'CartDrawer' ||
      el.id === 'CartDrawer-Overlay' ||
      el.classList?.contains('drawer') ||
      el.classList?.contains('cart-drawer') ||
      !!el.closest('cart-drawer')
    );
  };

  const dismissAnyCustomizerModals = (triggerEl) => {
    if (typeof window.__tms_dismissCustomizerModals === 'function') {
      window.__tms_dismissCustomizerModals(triggerEl);
      return;
    }

    // Fallback dismissal across all known customizers (ONLY success modals)
    document.querySelectorAll('.sc-wrapper-fixed, .custom-layer-mademine, .sc-custom-layer-content').forEach(m => {
      if (isDrawerElement(m)) return;
      m.hidden = true;
      m.style.setProperty('display', 'none', 'important');
      m.remove();
    });

    document.querySelectorAll('.sunzi__add-cart-success-mKaJC, [class*="sunzi__add-cart-success"], .sunzi__add-cart-success-modal-TfH66').forEach(m => {
      if (isDrawerElement(m)) return;
      const wrapper = m.closest('[class*="sunzi__wrapper"]') || m;
      wrapper.hidden = true;
      wrapper.style.setProperty('display', 'none', 'important');
      wrapper.remove();
    });

    document.querySelectorAll('[class*="add-cart-success"], [class*="cart-success"]').forEach(m => {
      if (isDrawerElement(m)) return;
      m.hidden = true;
      m.style.setProperty('display', 'none', 'important');
      m.remove();
    });

    document.documentElement.classList.remove('sc-lock', 'sc-lock-compatible-ios15', 'sunzi__lock-rxvLI');
    document.body.classList.remove('sc-lock', 'sc-lock-compatible-ios15', 'sunzi__lock-rxvLI');
    document.documentElement.style.removeProperty('overflow');
    document.body.style.removeProperty('overflow');
    document.documentElement.style.removeProperty('position');
    document.body.style.removeProperty('position');
  };

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const target = event.target;
    if (isDrawerElement(target) || target.id === 'cart-icon-bubble' || target.id === 'CartDrawer-Checkout') return;

    const candidate = target.closest('a[href*="/cart"], button, [role="button"], .__button, [class*="button"], [class*="btn"], div') || target;
    const text = (candidate.textContent || '').trim().toLowerCase();
    const href = candidate.getAttribute ? (candidate.getAttribute('href') || '') : '';
    const isCartLink = (href === '/cart' || href.endsWith('/cart') || href.includes('/cart?')) && !href.includes('/cart/add');
    const isCartText = (
      text === 'ver carrinho' ||
      text === 'ver carrinho e finalização da compra' ||
      text === 'ir para o carrinho' ||
      text === 'view cart' ||
      text === 'view cart and checkout' ||
      text === 'ver carrinho e finalizar compra' ||
      text.includes('ver carrinho') ||
      text.includes('finalização da compra') ||
      text.includes('finalizar compra')
    );

    if (isCartText || isCartLink) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        dismissAnyCustomizerModals(candidate);

        if (typeof drawer.refresh === 'function') {
          drawer.refresh().then(() => {
            dismissAnyCustomizerModals(candidate);
            if (typeof drawer.open === 'function') drawer.open(candidate);
          });
        } else if (typeof drawer.open === 'function') {
          drawer.open(candidate);
        }
      }
    }
  }, true);
})();
